// @ts-strict-ignore
import { ArticleConnectionPropertyValue } from '@article/constants/article-connection-property-values';
import { ProjectModel } from '../../../model/project.model';
import { Connection } from '@3d-scene/model3d/connection';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { TextureUtils } from '@3d-scene/model3d/util/texture-utils';
import { TextureConnectionSortGroupHelper } from '@3d-scene/helper/connection/texture-connection-sort-group.helper';
import { ArticleUsageVO, ArticleVO } from '@article/model/vo/article.vo';
import { PropertyUtils } from '@article/util/property-utils';
import { ArticleUsageInfoPropertyDef } from '@article/constants/article-usage-info-property-def';
import { CodingArticleUtils } from '@article/util/coding-article.utils';
import { ArticleManager } from '@article/manager/article.manager';
import { DimensionLayerElementCreator } from '@3d-scene/layer/creator/dimension-layer-element-creator';
import { UnitConverter } from '@3d-scene/common/util/unit-converter';
import { ArticleUtils } from '@article/util/article-utils';
import { Injectable } from '@angular/core';
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';
import { SceneManager } from '@3d-scene/manager/scene.manager';
import { MountingRailHelper } from '@project/helper/mounting-rail.helper';
import { ProjectDimensionsService } from '../../project/service/project-dimensions.service';
import { ArticleContainerUtils } from '@3d-scene/model3d/util/article-container-utils';
import { ConnectionInfoUtils } from '@3d-scene/model3d/util/connection-info-utils';
import { ArticleUsageService } from '@article/service/article-usage.service';
import { ProjectService } from '../../import-export/service/project.service';
import { IsProjectType } from '../../../model/is-project-type';
import { CollisionHelper } from '@3d-scene/helper/collision.helper';
import { GlobalArticleContainerUtils } from '@3d-scene/model3d/util/global-article-container-utils';

export class DirectPrintingData {
  series: string;
  printingLength: number;
}

export class LabelInfo {
  public labelCount: number;
  public notDefaultLabelCount: number;
  public directPrintingData: DirectPrintingData[];
}

@Injectable({ providedIn: 'root' })
export class PriceCalculationHelper {
  constructor(
    private projectModel: ProjectModel,
    private articleManager: ArticleManager,
    private textureConnectionSortGroupHelper: TextureConnectionSortGroupHelper,
    private localeResourceManager: LocaleResourceManager,
    private sceneManager: SceneManager,
    private projectDimensionService: ProjectDimensionsService,
    private mountingRailHelper: MountingRailHelper,
    private containerService: ContainerService,
    private articleContainerUtils: ArticleContainerUtils,
    private codingArticleUtils: CodingArticleUtils,
    private articleUsageService: ArticleUsageService,
    private projectService: ProjectService,
    private globalArticleContainerUtils: GlobalArticleContainerUtils,
  ) {}

  public getProjectWidth(): number {
    const dlec: DimensionLayerElementCreator = new DimensionLayerElementCreator(
      this.projectModel,
      this.containerService,
      this.localeResourceManager,
      this.sceneManager,
      this.projectDimensionService,
      this.mountingRailHelper,
      this.articleContainerUtils,
      this.articleUsageService,
      this.projectService,
      this.globalArticleContainerUtils,
    );
    const pWidth = dlec.getProjectWidth();
    return Math.floor(pWidth * 100) / 100;
  }

  public findLabelCount(): LabelInfo {
    const processedArticleGuids: string[] = [];
    let textureCubes: Connection[] = [];

    for (const usage of this.projectModel.getAllArticleUsages()) {
      const articleContainer: ArticleContainer = this.containerService.findArticleContainerInGlobal(usage.id);

      if (articleContainer.article && !ArticleUtils.isLabelingArticle(articleContainer.article)) {
        let articleTextureCubes: Connection[] = ConnectionInfoUtils.getTextConnectionCubesByMetaElements(
          articleContainer,
          articleContainer.article.metaElements
        );
        // filter empty text out
        articleTextureCubes = articleTextureCubes.filter((cube) => TextureUtils.hasConnectionTextureText(cube));
        textureCubes = textureCubes.concat(articleTextureCubes);
      }
      processedArticleGuids.push(articleContainer.articleGUID);
    }

    const textures: Connection[][] = this.getConnectionsByProjectType(textureCubes);
    const notDefaultLabelingCubes: number = textures.filter(
      (textureCubes) => !ConnectionInfoUtils.isDefaulLabelingConnectionCubes(textureCubes)
    ).length;
    const directPrintingData: DirectPrintingData[] = this.createDirectPrintingData(textures);

    return { labelCount: textures.length, notDefaultLabelCount: notDefaultLabelingCubes, directPrintingData };
  }

  public findBrokenCodingFingerCount(): number {
    let compositeArticleId: number;
    let compositeArticleVersion: number;
    let useStrippedInfo: boolean = true;
    let articleUsage: ArticleUsageVO;

    const initArticleid: number = this.projectModel.currentInfoProperties.getInt("mcsGdsInitArticleId");

    for (const usage of this.projectModel.getAllArticleUsages()) {
      useStrippedInfo = true;
      compositeArticleId = PropertyUtils.getNumberPropertyValue(usage.infoProperties, ArticleUsageInfoPropertyDef.STRIPPED_COMPOSITE_ARTICLE_ID);
      compositeArticleVersion = PropertyUtils.getIntPropertyValue(
        usage.infoProperties,
        ArticleUsageInfoPropertyDef.STRIPPED_COMPOSITE_ARTICLE_VERSION
      );

      if (isNaN(compositeArticleId) || isNaN(compositeArticleVersion)) {
        useStrippedInfo = false;
        compositeArticleId = PropertyUtils.getNumberPropertyValue(usage.infoProperties, ArticleUsageInfoPropertyDef.COMPOSITE_ARTICLE_ID);
        compositeArticleVersion = PropertyUtils.getIntPropertyValue(usage.infoProperties, ArticleUsageInfoPropertyDef.COMPOSITE_ARTICLE_VERSION);
      }
      if (compositeArticleId && compositeArticleId === initArticleid && !isNaN(compositeArticleVersion)) {
        articleUsage = usage;
        break;
      }
    }

    const compositeArticleVO: ArticleVO = this.articleManager.getCachedArticle(compositeArticleId, compositeArticleVersion);
    const compositePartContainer: ArticleContainer = this.containerService.findArticleContainerInGlobal(articleUsage.id);
    const compositePartUsages: ArticleUsageVO[] = this.containerService.getCompositePartArticleUsages(compositePartContainer, useStrippedInfo);
    const codingFingerList = this.codingArticleUtils.getCodingFingerList(
      compositePartUsages,
      compositeArticleVO,
      ArticleConnectionPropertyValue.CODING_FINGER_CONNECTION_TYPE,
      null
    );

    return codingFingerList.filter(finger => !finger.isInUse).length;
  }

  private getConnectionsByProjectType(textureCubes: Connection[]): Connection[][] {
    if (IsProjectType.mcsGds(this.projectModel.currentProjectType)) {
      return this.textureConnectionSortGroupHelper.splitConnectionCubesOnGroups(textureCubes, false, true);
    } else {
      let connectionGroups: Connection[][] = this.textureConnectionSortGroupHelper.splitConnectionCubesOnGroups(textureCubes, false, false);
      connectionGroups = this.concatGroupsWithEmptyLabelsBetween(connectionGroups);
      return connectionGroups;
    }
  }


  // Bei der Bepreisung der Direktbedruckung, müssen zusammengehörige Direktbedruckungsflächen ermittelt werden.
  // Dabei sollen Bedruckungsflächen, die Lücken beinhalten, als eine Fläche behandelt werden.
  private concatGroupsWithEmptyLabelsBetween (connections: Connection[][]): Connection[][] {
    const sortedGroups = this.sortGroups(connections);
    const connectionGroups: Connection[][] = [];
    for (const nextGroup of sortedGroups) {
      let isGroupNotConcated: boolean = true;
      for (const currentGroup of connectionGroups) {
        const connectionsBetween = this.getDirectPrintConectionBetween(currentGroup, nextGroup);
        if (connectionsBetween.length > 0) {
          connectionsBetween.forEach( con => currentGroup.push(con));
          nextGroup.forEach( con => currentGroup.push(con));
          isGroupNotConcated = false;
          break;
        }
      }

      if (isGroupNotConcated) {
        connectionGroups.push(nextGroup);
      }
    }
    return connectionGroups;
  }

  private sortGroups(connectionGroups: Connection[][]): Connection[][] {
    const firstConnections: Connection[] = connectionGroups.map(g => g[0]);
    const sortedFirstConnections: Connection[] = ConnectionInfoUtils.sortConnections(firstConnections, true);
    const sortedGroups: Connection[][] = [];

    for (const connection of sortedFirstConnections) {
      for (const group of connectionGroups) {
        if (group[0].uid === connection.uid) {
          sortedGroups.push(group);
        }
      }
    }
    return sortedGroups;
  }

  private getDirectPrintConectionBetween(previousGroup: Connection[], currentGroup: Connection[]): Connection[] {
    const previousCube: Connection = previousGroup[previousGroup.length -1];
    const currentCube: Connection = currentGroup[0];

    const connectionsBetween: Connection[] = this.findTextConnectionsBetween(previousCube, currentCube);

    if (connectionsBetween.length === 0) {
      return [];
    }

    //Prüfen ob die Connections tatsächlich zusammengehören/aneinader grenzen.
    let previousConnection = connectionsBetween[0];
    for (const nextConnection of connectionsBetween.slice(1) ) {
      if (!this.textureConnectionSortGroupHelper.isSingleGroupOfConnectionCubeByLocation(previousConnection, nextConnection, false, false)) {
        return [];
      }
      previousConnection = nextConnection;
    }

    return connectionsBetween.slice(1, connectionsBetween.length - 1);
  }

  // Ermittelt die Texture Connections zwischen previous und current Cube inkl. dieser beiden.
  private findTextConnectionsBetween(previousCube: Connection, currentCube: Connection) {
    let collidedConnections: Connection[] = CollisionHelper.findCollisionConnectionsInGlobal(previousCube, currentCube, this.containerService, true, false, false, true);
    collidedConnections = ConnectionInfoUtils.sortConnections(collidedConnections, true);
    const connectionsBetween: Connection[] = []
    for (const connection of collidedConnections ) {
      if (!ConnectionInfoUtils.isTextConnectionType(connection.type)) {
        continue;
      }
      connectionsBetween.push(connection);
    }
    return connectionsBetween;
  }

  private createDirectPrintingData(textures: Connection[][]): DirectPrintingData[] {
    const directPrintingData: DirectPrintingData[] = [];
    for (const label of textures) {
      const data: DirectPrintingData = new DirectPrintingData();

      data.series = label[0].articleContainer.article.series;

      const length = label.map((l) => l.compositionRectangle.width).reduce((a, b) => a + b);
      data.printingLength = UnitConverter.fromTo('3d', 'mm', length);

      directPrintingData.push(data);
    }
    return directPrintingData;
  }
}
